import { shouldNeverHappen } from '@moojo/misc-constructs'

import type { CardDefinition } from './card-definition'

export type ValidationResult = {
  isValid: boolean
  card?: CardDefinition
  errors: ValidationError[]
}

export type ValidationError = {
  field: string
  message: string
}

/**
 * Validates if a field value meets the constraints defined in a card's field definition
 * @param value The value to validate
 * @param fieldName The name of the field
 * @param card The card definition containing the field
 * @returns An object with isValid flag and optional error message
 */
function validateFieldValue(
  value: unknown,
  fieldName: string,
  card: CardDefinition,
): { isValid: boolean; error?: string } {
  // Find the field definition in the card
  const fieldDef = card.fields.find(f => f.name === fieldName)

  // If field doesn't exist in the card definition, it's not valid
  if (!fieldDef) {
    return {
      isValid: false,
      error: `Field '${fieldName}' is not defined for card '${card.cardName}'`,
    }
  }

  const legalValues = fieldDef.legalValues

  // Check if value meets the type and range constraints
  switch (legalValues.type) {
    case 'integer':
      if (typeof value !== 'number' || !Number.isInteger(value)) {
        return { isValid: false, error: 'Value must be an integer' }
      }

      if (legalValues.range && (value < legalValues.range[0] || value > legalValues.range[1])) {
        return {
          isValid: false,
          error: `Value must be between ${legalValues.range[0]} and ${legalValues.range[1]}`,
        }
      }

      return { isValid: true }

    case 'decimal':
      if (typeof value !== 'number') {
        return { isValid: false, error: 'Value must be a number' }
      }

      if (legalValues.range && (value < legalValues.range[0] || value > legalValues.range[1])) {
        return {
          isValid: false,
          error: `Value must be between ${legalValues.range[0]} and ${legalValues.range[1]}`,
        }
      }

      return { isValid: true }

    case 'enum':
      if (typeof value !== 'string') {
        return { isValid: false, error: 'Value must be a string' }
      }

      if (legalValues.options && !legalValues.options.includes(value)) {
        return {
          isValid: false,
          error: `Value must be one of: ${legalValues.options.join(', ')}`,
        }
      }

      return { isValid: true }

    default:
      shouldNeverHappen(legalValues)
  }
}

/**
 * Checks if an unknown input matches any card definition from the catalog
 * @param input - The input to validate against card definitions
 * @returns Validation result with matched card and any validation errors
 */
export function validateCardInput(input: unknown, defintions: CardDefinition[]): ValidationResult {
  const errors: ValidationError[] = []

  // Basic validation - must be an object
  if (!input || typeof input !== 'object') {
    return {
      isValid: false,
      errors: [{ field: 'input', message: 'Input must be an object' }],
    }
  }

  const inputObj = input as Record<string, unknown>

  // Input must have a name field
  if (!inputObj.name || typeof inputObj.name !== 'string') {
    return {
      isValid: false,
      errors: [{ field: 'name', message: 'Input must have a name property of type string' }],
    }
  }

  // Find the matching card in the catalog by comparing the name field
  // with the cardName property in the catalog
  const matchedCard = defintions.find(card => card.cardName === inputObj.name)

  if (!matchedCard) {
    return {
      isValid: false,
      errors: [{ field: 'name', message: `No card definition found with name '${inputObj.name}'` }],
    }
  }

  // Check all fields in the input object against the card definition
  // Treat all fields as optional, but validate the ones that are present
  for (const [fieldName, fieldValue] of Object.entries(inputObj)) {
    // Skip the name field since we already validated it
    if (fieldName === 'name') {
      continue
    }

    const { isValid, error } = validateFieldValue(fieldValue, fieldName, matchedCard)
    if (!isValid && error) {
      errors.push({ field: fieldName, message: error })
    }
  }

  return {
    isValid: errors.length === 0,
    card: matchedCard,
    errors,
  }
}
